import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { catchError, from, map, Observable, throwError } from 'rxjs';
import { ConfigService } from './config.service.';
import type { IApiResponse } from './http-new.interfaces';
import type { THttpHeadersRecord, TQueryParams } from './http-new.types';
import { Utils } from '../utils';
import { ClientSessionService } from './client-session.service';
export const tokenEventName = 'getAuth:token';

@Injectable({
    providedIn: 'root',
})
export class HttpNewService {
    public constructor(
        private configS: ConfigService,
        private router: Router,
        private clientSession: ClientSessionService,
    ) {}

    /**
     * Формирование заголовков. Возвращает строго типизированный объект.
     */
    private createHeaders(
        isFile: boolean,
        authToken?: string | null,
    ): THttpHeadersRecord {
        const headers: THttpHeadersRecord = {
            'X-Requested-With': 'XMLHttpRequest',
        };

        // Логика авторизации
        if (authToken && authToken.length > 10) {
            headers['Authorization'] = `TOKEN ${authToken}`;
        } else {
            const sessionTokenAuth = sessionStorage.getItem(
                'sessionTokenAuthAriadna',
            );
            headers['sessionToken'] = sessionTokenAuth ?? 'tokenExp';
        }

        if (!isFile) {
            headers['Content-Type'] = 'application/json';
        }

        return headers;
    }

    /**
     * Универсальный метод запроса с полной типизацией
     * T - тип данных, которые мы ожидаем в поле data ответа
     * B - тип тела запроса (для POST/PUT)
     */
    private request<T, B = unknown>(
        method: Method,
        url: string,
        data?: B | FormData,
        options?: {
            queryParams?: TQueryParams;
            isArrayBuffer?: boolean;
            authToken?: string | null;
            isFile?: boolean;
            signal?: AbortSignal;
        },
    ): Observable<T> {
        const isFile = options?.isFile ?? false;
        const isArrayBuffer = options?.isArrayBuffer ?? false;

        const config: AxiosRequestConfig = {
            method,
            url: this.configS.getValue('hostBackend') + url,
            headers: this.createHeaders(isFile, options?.authToken),
            withCredentials: true,
            params: options?.queryParams,
            signal: options?.signal,
        };

        if (isArrayBuffer) {
            config.responseType = 'arraybuffer';
        }

        if (data && method !== 'GET') {
            config.data = data;
        }

        return from(axios.request<B, AxiosResponse<unknown>>(config)).pipe(
            map((response) => {
                // 1. Обработка бинарных данных
                if (isArrayBuffer) {
                    return response.data as unknown as T;
                }

                // 2. Обработка JSON (ответ может прийти строкой или объектом)
                let responseData: unknown = response.data;

                if (typeof responseData === 'string') {
                    try {
                        responseData = JSON.parse(responseData);
                    } catch (e) {
                        console.error('JSON Parse Error', e);
                        throw new Error('Invalid JSON response from server');
                    }
                }

                // 3. Проверка структуры ответа
                // Мы проверяем, есть ли поле data, чтобы понять, что это наш wrapper ApiResponse
                if (this.isApiResponse(responseData)) {
                    const wrappedResponse = responseData as IApiResponse<T>;

                    if (!wrappedResponse.success) {
                        console.warn('API Logic Error', wrappedResponse);
                        const apiError = new Error(
                            wrappedResponse.message ||
                                wrappedResponse.msg ||
                                'API Error',
                        );
                        Object.assign(apiError, wrappedResponse);
                        throw apiError;
                    }

                    return wrappedResponse.data as T;
                }

                // Если ответ не в обертке, возвращаем как есть (приведение к T на ответственности вызывающего)
                return responseData as T;
            }),
            catchError((error: unknown) => {
                if (Utils.errorTypeGuard(error).statusCode === 401) {
                    this.clientSession.clear();
                    if (!url.includes('/user/auth/logout')) {
                        this.router
                            .navigate(['/auth/login'])
                            .catch((reason) =>
                                console.error(
                                    'Failed to redirect after unauthorized response',
                                    reason,
                                ),
                            );
                    }
                }

                // Единая обработка ошибок axios
                console.error('HTTP Error', error);
                return throwError(() => error);
            }),
        );
    }

    /**
     * Type Guard для проверки структуры ответа
     */
    private isApiResponse(data: unknown): data is IApiResponse<unknown> {
        return (
            typeof data === 'object' &&
            data !== null &&
            'success' in data &&
            'data' in data
        );
    }

    public get<T = unknown>(
        url: string,
        queryParams?: TQueryParams | null,
        isArrayBuffer?: boolean,
        authToken?: string | null,
        signal?: AbortSignal,
    ): Observable<T> {
        return this.request<T>('GET', url, undefined, {
            queryParams: queryParams ?? undefined,
            isArrayBuffer,
            authToken,
            signal,
        });
    }

    public post<T = unknown, B = unknown>(
        url: string,
        data: B | FormData | null,
        isFile = false,
    ): Observable<T> {
        const safeData = data === null ? undefined : data;
        return this.request<T, B>('POST', url, safeData, { isFile });
    }

    public put<T = unknown, B = unknown>(
        url: string,
        data: B,
        isFile: boolean = false,
    ): Observable<T> {
        return this.request<T, B>('PUT', url, data, { isFile });
    }

    public delete<T = unknown, B = unknown>(
        url: string,
        data?: B,
    ): Observable<T> {
        return this.request<T, B>('DELETE', url, data, {});
    }
}
